import { Injectable } from '@angular/core';
import { Router, Route } from '@angular/router';
import {
  CollectionRoomType,
  ICollection,
  IEventGeneralSettings,
  IKeynote,
  INavigationLink,
  INetworking,
  IReception,
  IRegistration,
  ITradeFair,
  IWorkshop,
  NavigationLinkType,
  RegistrationType,
  StreamingTool
} from '@sv/interfaces';
import { NetworkingComponent } from '../_components/_networking/networking.component';
import { ReceptionComponent } from '../_components/_reception/reception/reception.component';
import { KeynoteComponent } from '../_components/_streaming/keynote/keynote.component';
import { TradefairOverviewComponent } from '../_components/_tradefair/tradefair-overview/tradefair-overview.component';
import { WorkshopOverviewComponent } from '../_components/_workshop/workshop-overview/workshop-overview.component';
import { customApp } from '../_configuration/configuration';
import { AuthAttendeeGuard } from '../_helpers/authAttendee.guard';
import { ColorService } from './_content/color.service';
import { TicketcodeAuthService } from './_auth/ticketcode-auth.service';
import { LoginService } from './_content/login.service';
import { NavigationService } from './_content/navigation.service';
import { RegistrationService } from './_content/registration.service';
import { ReceptionService } from './_content/reception.service';
import { KeynoteService } from './_content/keynote.service';
import { WorkshopService } from './_content/workshop.service';
import { NetworkingService } from './_content/networking.service';
import { TradefairService } from './_content/_TradeFair/tradefair.service';
import { GeneralSettingsService } from './_content/general-settings.service';
import { EventAnalyticsService } from './_analytics/event-analytics.service';
import { CollectionService } from './_content/collection.service';
import { CollectionComponent } from '../_components/collection/collection.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LandingpageComponent } from '../_components/landingpage/landingpage.component';
import { v5 as uuidv5 } from 'uuid';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../environments/environment.prod';
import { Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class InitAppService {
  links: INavigationLink[];
  constructor(
    private router: Router,
    private navigationService: NavigationService,
    private registrationService: RegistrationService,
    private loginService: LoginService,
    private colorService: ColorService,
    private ticketcodeAuthService: TicketcodeAuthService,
    private receptionService: ReceptionService,
    private keynoteService: KeynoteService,
    private workshopService: WorkshopService,
    private networkingService: NetworkingService,
    private fairService: TradefairService,
    private generalSettingsService: GeneralSettingsService,
    private eventAnalyticsService: EventAnalyticsService,
    private collectionService: CollectionService,
    private snackbar: MatSnackBar,
    private titleService: Title
  ) {}

  initApp(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.getData();
      } catch (error) {
        this.snackbar.open('Ein Fehler ist aufgetreten. Bitte versuchen Sie die Seite neu zu laden.', 'Schließen');
        reject(false);
      }
      resolve(true);
    });
  }

  private async getData() {
    if (typeof customApp.colors === 'string') {
      customApp.colors = await this.colorService.getColorsById(customApp.colors);
    }
    if (typeof customApp.registration === 'string') {
      customApp.registration = await this.registrationService.getRegistrationById(customApp.registration);
    }
    if (typeof customApp.login === 'string') {
      customApp.login = await this.loginService.getLoginById(customApp.login);
    }

    if (typeof customApp.generalSettings === 'string') {
      customApp.generalSettings = await this.generalSettingsService.getGeneralSettings(customApp.generalSettings);
    }

    if ((customApp.generalSettings as IEventGeneralSettings).enableLP) {
      this.router.config[0] = { path: '', component: LandingpageComponent };
    }

    // as soon as we have the logo/extra favicon in the general settings object we can show it here
    /* const favIcon: HTMLLinkElement = document.querySelector('#favIcon');
    favIcon.href =
      'https://upload.wikimedia.org/wikipedia/commons/thumb/7/73/Stonewall_Inn%2C_April_2019.jpg/143px-Stonewall_Inn%2C_April_2019.jpg'; */

    this.titleService.setTitle(customApp.generalSettings.title);

    // only load remaining data if already authenticated otherwise the user will be routed to login/register
    if (customApp.registration.enabled || customApp.login.enabled) {
      const authenticated = this.ticketcodeAuthService.autoAuthUser();
      if (authenticated) {
        if (!localStorage.getItem('uniqueAnonymousId')) {
          const uniqueAnonymousId = uuidv5(this.ticketcodeAuthService.decodeToken()?._id ?? uuidv4(), environment.MY_HASH_NAMESPACE);
          localStorage.setItem('uniqueAnonymousId', uniqueAnonymousId);
          console.log(uniqueAnonymousId);
        }
        await this.loadRemainingData(false);
      }
      if ((customApp.registration as IRegistration).type === RegistrationType.EVENTBRITE) {
        await this.dynamicallyLoadScript('https://www.eventbrite.com/static/widgets/eb_widgets.js');
      }
      return;
    }

    if (customApp.eventId === '61646d149cc14631ad3bb892') {
      const params = new URLSearchParams(window.location.search);
      const firstName = params.get('firstName');
      const lastName = params.get('lastName');
      const email = params.get('email');
      if (email && firstName && lastName) {
        this.router.navigate(['/login'], { queryParams: { firstName, lastName, email } });
        return;
      }
    }

    // If open platform and landing page dont automatically log in but show landingpage,
    // if routed to a subpage e.g. reception login and show module
    if ((customApp.generalSettings as IEventGeneralSettings).enableLP && location.pathname === '/') {
      return;
    }

    // "Login" if platform is open = no login or registration. Backend checks whether this is actually true.

    // generate random uuid bc attendee id would always be the same
    if (!localStorage.getItem('uniqueAnonymousId')) {
      const uniqueAnonymousId = uuidv5(uuidv4(), environment.MY_HASH_NAMESPACE);
      localStorage.setItem('uniqueAnonymousId', uniqueAnonymousId);
    }
    await this.ticketcodeAuthService.promiseLogin('4711', customApp.eventId);
    await this.loadRemainingData(false);
  }

  async loadRemainingData(route: boolean) {
    if (typeof customApp.navigation === 'string') {
      const navigation = await this.navigationService.getCompleteNavigationById(customApp.navigation);
      this.links = navigation.links as INavigationLink[];
      this.addRoutes();
      this.prepoluateFirstModule();
      this.ticketcodeAuthService.setAuthenticated();
      const favIcon: HTMLLinkElement | null = document.querySelector('#favIcon');
      const appleIcon: HTMLLinkElement | null = document.querySelector('#appleIcon');
      if (favIcon) {
        favIcon.href = navigation.logo;
      }
      if (appleIcon) {
        appleIcon.href = navigation.logo;
      }
    }
    await this.eventAnalyticsService.newEventView(customApp.eventId, navigator.userAgent);
    await this.eventAnalyticsService.startLinkDuration(this.links[0]._id, customApp.eventId);
    if (route) {
      this.router.navigate([this.links[0].route]);
    }
    if (typeof customApp.generalSettings === 'string') {
      await this.generalSettingsService.getGeneralSettings(customApp.generalSettings);
    }
  }

  prepoulateContent(link: INavigationLink, collectionRoomId?: string, collectionRoomType?: CollectionRoomType) {
    switch (link.type) {
      case NavigationLinkType.Reception:
        this.receptionService.setReception(link.receptionSettings);
        break;
      case NavigationLinkType.Keynote:
        this.keynoteService.setKeynote(link.keynoteSettings);
        break;
      case NavigationLinkType.Workshop:
        this.workshopService.setWorkshop(link.workshopSettings);
        break;
      case NavigationLinkType.Networking:
        this.networkingService.setNetworking(link.networkingSettings);
        break;
      case NavigationLinkType.Fair:
        this.fairService.setFair(link.fairSettings);
        break;
      case NavigationLinkType.Collection:
        this.collectionService.setCollection(link.collectionSettings);
        if (collectionRoomId) {
          switch (collectionRoomType) {
            case CollectionRoomType.EXPO:
              const expoIndex = (link.collectionSettings as ICollection).rooms.findIndex(
                (room) => (room.expoId as ITradeFair)?._id === collectionRoomId
              );
              if (expoIndex !== -1) {
                this.fairService.setFair((link.collectionSettings as ICollection).rooms[expoIndex].expoId as ITradeFair);
              }
              break;
            case CollectionRoomType.KEYNOTE:
              const keynoteIndex = (link.collectionSettings as ICollection).rooms.findIndex(
                (room) => (room.keynoteId as IKeynote)?._id === collectionRoomId
              );
              if (keynoteIndex !== -1) {
                this.keynoteService.setKeynote((link.collectionSettings as ICollection).rooms[keynoteIndex].keynoteId as IKeynote);
              }
            case CollectionRoomType.NETWORKING:
              const networkingIndex = (link.collectionSettings as ICollection).rooms.findIndex(
                (room) => (room.networkingId as INetworking)?._id === collectionRoomId
              );
              if (networkingIndex !== -1) {
                this.networkingService.setNetworking(
                  (link.collectionSettings as ICollection).rooms[networkingIndex].networkingId as INetworking
                );
              }
            case CollectionRoomType.WORKSHOP:
              const workshopIndex = (link.collectionSettings as ICollection).rooms.findIndex(
                (room) => (room.workshopId as IWorkshop)?._id === collectionRoomId
              );
              if (workshopIndex !== -1) {
                this.workshopService.setWorkshop((link.collectionSettings as ICollection).rooms[workshopIndex].workshopId as IWorkshop);
              }
            case CollectionRoomType.RECEPTION:
              const receptionIndex = (link.collectionSettings as ICollection).rooms.findIndex(
                (room) => (room.receptionId as IReception)?._id === collectionRoomId
              );
              if (receptionIndex !== -1) {
                this.receptionService.setReception((link.collectionSettings as ICollection).rooms[receptionIndex].receptionId as IReception);
              }
            case CollectionRoomType.COLLECTION:
              const collectionIndex = (link.collectionSettings as ICollection).rooms.findIndex(
                (room) => (room.collectionId as ICollection)?._id === collectionRoomId
              );
              if (collectionIndex !== -1) {
                this.collectionService.setCollection(
                  (link.collectionSettings as ICollection).rooms[collectionIndex].collectionId as ICollection
                );
              }
            default:
              break;
          }
        }
        break;
    }
  }

  async addRoutes(links?: INavigationLink[]) {
    if (links) {
      this.links = links;
    }
    // remove default and catch all since we need to reconfigure based on the event
    const initialRoutes = this.router.config.filter((val) => val.path !== '' && val.path !== '/' && val.path !== '**');
    this.router.resetConfig(initialRoutes);

    const lpActivated = (customApp.generalSettings as IEventGeneralSettings).enableLP;
    if (lpActivated) {
      const defaultRoute: Route = { path: '', component: LandingpageComponent };
      this.router.config.push(defaultRoute);
    }

    for (let index = 0; index < this.links.length; index++) {
      const link = this.links[index];
      switch (link.type) {
        case NavigationLinkType.Reception:
          if (index === 0 && !lpActivated) {
            const defaultRoute: Route = { path: '/', component: ReceptionComponent };
            this.router.config.push(defaultRoute);
          }
          const receptionRoute: Route = { path: link.route, component: ReceptionComponent, canActivate: [AuthAttendeeGuard] };
          this.router.config.push(receptionRoute);
          break;
        case NavigationLinkType.Keynote:
          if (index === 0 && !lpActivated) {
            const defaultRoute: Route = { path: '/', component: KeynoteComponent };
            this.router.config.push(defaultRoute);
          }

          if ((link.keynoteSettings as IKeynote).streamingTool === StreamingTool.Wowza) {
            await this.dynamicallyLoadStyles('https://cdn.myth.theoplayer.com/3c5a2a06-7d4c-49ba-98bb-1f8ea34a5981/ui.css');
          }
          const keynoteRoute: Route = { path: link.route, component: KeynoteComponent, canActivate: [AuthAttendeeGuard] };
          this.router.config.push(keynoteRoute);
          break;
        case NavigationLinkType.Workshop:
          if (index === 0 && !lpActivated) {
            const defaultRoute: Route = { path: '/', component: WorkshopOverviewComponent };
            this.router.config.push(defaultRoute);
          }
          const workshopRoute: Route = { path: link.route, component: WorkshopOverviewComponent, canActivate: [AuthAttendeeGuard] };
          this.router.config.push(workshopRoute);
          break;
        case NavigationLinkType.Networking:
          if (index === 0 && !lpActivated) {
            const defaultRoute: Route = { path: '/', component: NetworkingComponent };
            this.router.config.push(defaultRoute);
          }
          const networkingRoute: Route = { path: link.route, component: NetworkingComponent, canActivate: [AuthAttendeeGuard] };
          this.router.config.push(networkingRoute);
          break;
        case NavigationLinkType.Fair:
          if (index === 0 && !lpActivated) {
            const defaultRoute: Route = { path: '/', component: TradefairOverviewComponent };
            this.router.config.push(defaultRoute);
          }
          const fairRoute: Route = { path: link.route, component: TradefairOverviewComponent, canActivate: [AuthAttendeeGuard] };
          this.router.config.push(fairRoute);
          break;
        case NavigationLinkType.Collection:
          if (index === 0 && !lpActivated) {
            const defaultRoute: Route = { path: '/', component: CollectionComponent };
            this.router.config.push(defaultRoute);
          }
          const collectionRoute: Route = {
            path: link.route,
            component: CollectionComponent,
            canActivate: [AuthAttendeeGuard]
          };
          for (const room of (link.collectionSettings as ICollection).rooms) {
            switch (room.type) {
              case CollectionRoomType.EXPO:
                const expo2 = room.expoId as ITradeFair;
                const expoLink: Route = { path: `${link.route}/${expo2._id}`, component: TradefairOverviewComponent };
                this.router.config.push(expoLink);
                break;
              case CollectionRoomType.KEYNOTE:
                const keynote = room.keynoteId as IKeynote;
                const keynoteLink: Route = { path: `${link.route}/${keynote._id}`, component: KeynoteComponent };
                this.router.config.push(keynoteLink);
                break;
              case CollectionRoomType.NETWORKING:
                const networking = room.networkingId as INetworking;
                const networkingLink: Route = { path: `${link.route}/${networking._id}`, component: NetworkingComponent };
                this.router.config.push(networkingLink);
                break;
              case CollectionRoomType.WORKSHOP:
                const workshop = room.workshopId as IWorkshop;
                const workshopLink: Route = { path: `${link.route}/${workshop._id}`, component: WorkshopOverviewComponent };
                this.router.config.push(workshopLink);
                break;
              case CollectionRoomType.RECEPTION:
                const reception = room.receptionId as IReception;
                const receptionLink: Route = { path: `${link.route}/${reception._id}`, component: ReceptionComponent };
                this.router.config.push(receptionLink);
                break;
              case CollectionRoomType.COLLECTION:
                const collection = room.collectionId as ICollection;
                const collectionLink: Route = { path: `${link.route}/${collection._id}`, component: CollectionComponent };
                this.router.config.push(collectionLink);
                break;
              default:
                break;
            }
          }
          this.router.config.push(collectionRoute);
          break;
      }
    }

    // TODO passiert else fall überhaupt
    if (this.links[0]) {
      const catchAllRoute: Route = { path: '**', redirectTo: this.links[0].route };
      this.router.config.push(catchAllRoute);
    } else {
      const catchAllRoute: Route = { path: '**', redirectTo: 'reception' };
      this.router.config.push(catchAllRoute);
    }
  }

  private prepoluateFirstModule() {
    const link = this.links[0];
    switch (link.type) {
      case NavigationLinkType.Reception:
        this.receptionService.setReception(link.receptionSettings);
        break;
      case NavigationLinkType.Keynote:
        this.keynoteService.setKeynote(link.keynoteSettings);
        break;
      case NavigationLinkType.Workshop:
        this.workshopService.setWorkshop(link.workshopSettings);
        break;
      case NavigationLinkType.Networking:
        this.networkingService.setNetworking(link.networkingSettings);
        break;
      case NavigationLinkType.Fair:
        this.fairService.setFair(link.fairSettings);
        break;
      case NavigationLinkType.Collection:
        this.collectionService.setCollection(link.collectionSettings);
        break;
    }
  }

  private async dynamicallyLoadScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const scriptEl = document.createElement('script');
      scriptEl.src = url;
      scriptEl.type = 'text/javascript';
      scriptEl.async = true;

      scriptEl.onload = () => {
        resolve();
      };
      scriptEl.onerror = (error: any) => reject(error.toString());
      document.body.append(scriptEl);
    });
  }

  private dynamicallyLoadStyles(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const style = document.createElement('link');
      style.href = url;
      style.rel = 'stylesheet';
      style.type = 'text/css';

      style.onload = () => {
        resolve();
      };
      style.onerror = (error: any) => reject(error.toString());
      document.body.append(style);
    });
  }
}
